import {
  ChordStabs,
  Composer,
  ImpactFX,
  LeadSynth,
  PadSynth,
  ProceduralBeat,
  RiserFX,
  SubBass,
  WobbleBass,
} from "@engine";
import type {
  App,
  BarContext,
  QualitySettings,
  SectionKind,
  SectionStartEvent,
  Song,
} from "@engine";
import type { ThemeService } from "../services/ThemeService";
import type { ScoreSystem } from "../scoring/ScoreSystem";
import type { LevelStreamer } from "../level/LevelStreamer";
import type { Player } from "../player/Player";
import type { MusicConfig } from "../config/themes";
import { getSong } from "./songs";

// MusicDirector orchestrates the entire melodic-dubstep mix:
//   - owns a Composer (chord progression + section state machine)
//   - owns 5 synth layers + 2 FX (RiserFX, ImpactFX) + drum kit
//   - subscribes to ThemeService.onMusicConfig to load a curated song
//   - subscribes to ScoreSystem.onComboChanged to multiply layer
//     intensity (0.55..1.0) -- combo no longer GATES layers, the song
//     plays its full arrangement on its own
//   - subscribes to Composer.onBar for drum pattern + level switches
//   - subscribes to Composer.onSectionStart for crash + impact + riser

// Per-section base mix levels for each layer. The Composer's
// sectionEnergy is implicit in these numbers; we keep the explicit
// table for clarity.
//
// rows = SectionKind, cols = layer
interface LayerLevels {
  sub: number;
  pad: number;
  lead: number;
  wobble: number;
  stabs: number;
}

const SECTION_LEVELS: Record<SectionKind, LayerLevels> = {
  intro:  { sub: 0.0,  pad: 0.40, lead: 0.0,  wobble: 0.0,  stabs: 0.0  },
  verse:  { sub: 0.70, pad: 0.55, lead: 0.50, wobble: 0.0,  stabs: 0.0  },
  build:  { sub: 0.70, pad: 0.50, lead: 0.55, wobble: 0.45, stabs: 0.0  },
  drop:   { sub: 0.85, pad: 0.45, lead: 0.70, wobble: 0.70, stabs: 0.65 },
  bridge: { sub: 0.50, pad: 0.55, lead: 0.45, wobble: 0.40, stabs: 0.0  },
  outro:  { sub: 0.30, pad: 0.40, lead: 0.30, wobble: 0.0,  stabs: 0.0  },
};

// Combo -> intensity multiplier. Sub + pad always at 1.0 because
// removing them entirely makes the song feel broken; lead, wobble and
// stabs scale 0.55..1.0 by combo so the player still feels rewarded.
function comboIntensity(combo: number): number {
  // 0..6 combo -> 0.55..1.0 linear, then capped at 1.0.
  const t = Math.min(combo / 6, 1);
  return 0.55 + 0.45 * t;
}

// Long ramp for the pad so it doesn't snap audibly. Fast for everything
// else so section transitions sound deliberate.
const PAD_RAMP_MS = 1200;
const STD_RAMP_MS = 250;

export class MusicDirector {
  private app: App;
  private beat: ProceduralBeat;
  private themes: ThemeService;
  private score: ScoreSystem;
  // Kept for forward compat: future section-aware reactions to gate
  // crossings could live here. The Composer owns harmonic motion now.
  private streamer: LevelStreamer;
  private player: Player;
  private quality: QualitySettings;

  private composer: Composer;
  private sub: SubBass;
  private wobble: WobbleBass;
  private pad: PadSynth;
  private lead: LeadSynth;
  private stabs: ChordStabs;
  private riser: RiserFX;
  private impact: ImpactFX;

  private currentSong: Song | null = null;
  private currentSection: SectionKind = "intro";
  private intensity = 1; // current combo-driven multiplier
  private silenced = false;
  private started = false;
  private unsubs: Array<() => void> = [];
  // External intensity multiplier applied on top of the combo-driven
  // intensity. Used by Frenzy + Boss to push the mix above the normal
  // combo cap for a fixed duration. forceDropUntil is the audio-time
  // timestamp until which we additionally pin section to "drop".
  private intensityBoostMul = 1;
  private intensityBoostUntil = 0;
  private forceDropUntil = 0;
  // True when MorphHexGame has called cueBoss() since the last bar
  // boundary. We trigger a riser on the next onBar so it lines up
  // with the music's downbeat instead of cutting in mid-bar.
  private bossRiserPending = false;
  // When set to "sub", every layer except the sub bass is clamped to
  // 0 in applyMix. Used by the daily "sub_only" modifier so the run
  // sounds like a stripped-down dub mix without us having to author a
  // bespoke song. null = no cap applied.
  private layerCap: "sub" | null = null;

  constructor(
    app: App,
    beat: ProceduralBeat,
    themes: ThemeService,
    score: ScoreSystem,
    streamer: LevelStreamer,
    player: Player,
    quality: QualitySettings
  ) {
    this.app = app;
    this.beat = beat;
    this.themes = themes;
    this.score = score;
    this.streamer = streamer;
    this.player = player;
    this.quality = quality;

    this.composer = new Composer(this.beat);
    this.sub = new SubBass(this.app.audio, this.beat);
    this.wobble = new WobbleBass(this.app.audio, this.beat);
    this.pad = new PadSynth(this.app.audio);
    this.lead = new LeadSynth(this.app.audio, this.beat);
    this.stabs = new ChordStabs(this.app.audio, this.beat);
    this.riser = new RiserFX(this.app.audio);
    this.impact = new ImpactFX(this.app.audio);

    // Wire subscriptions immediately so we react even if start() is
    // deferred until after audio resume.
    this.unsubs.push(
      this.themes.onMusicConfig.on((cfg) => this.onMusicConfigChanged(cfg))
    );
    this.unsubs.push(
      this.score.onComboChanged.on((c) => this.onComboChanged(c))
    );
    this.unsubs.push(this.composer.onBar.on((ctx) => this.onBar(ctx)));
    this.unsubs.push(
      this.composer.onSectionStart.on((e) => this.onSectionStart(e))
    );
    this.unsubs.push(this.player.onDied.on(() => this.silence(200)));

    // Touch streamer so TS keeps it in the constructor signature even
    // while we don't react to gates yet (kept for forward-compat).
    void this.streamer;
  }

  // Starts the entire mix. Idempotent. Must be called AFTER
  // app.audio.start() so the AudioContext exists.
  start(): void {
    if (this.started) return;
    if (!this.app.audio.context) return;

    this.sub.start();
    this.wobble.start();
    this.lead.start();
    this.pad.start();
    if (this.canUseStabs()) this.stabs.start();

    // Bind layers BEFORE starting the composer so they get the priming
    // BarContext + onSectionStart emit and start with the right chord.
    this.sub.bindComposer(this.composer);
    this.wobble.bindComposer(this.composer);
    this.lead.bindComposer(this.composer);
    this.pad.bindComposer(this.composer);
    if (this.canUseStabs()) this.stabs.bindComposer(this.composer);

    // Quality-tier delay-tail toggle for the lead.
    this.lead.setDelayTailEnabled(this.canUseLeadTail());

    // Pick the song for the active theme and start the composer.
    const cfg = this.themes.getMusicConfig();
    this.loadSong(cfg);

    this.started = true;
  }

  // Mute every layer (drums keep playing). Used while paused / on death.
  silence(rampMs = 200): void {
    this.silenced = true;
    if (!this.started) return;
    this.sub.setLevel(0, rampMs);
    this.wobble.setLevel(0, rampMs);
    this.lead.setLevel(0, rampMs);
    this.pad.setLevel(0, rampMs);
    this.stabs.setLevel(0, rampMs);
  }

  // Re-apply the current section + intensity. Called when leaving
  // pause or continuing after a slow-down.
  restoreFromCombo(): void {
    this.silenced = false;
    if (!this.started) return;
    this.applyMix(STD_RAMP_MS);
  }

  // Frenzy + Boss layer this on top of the regular combo-driven mix.
  // mul is multiplied into lead/wobble/stab gains; durationSec also
  // pins the section state to "drop" so the boost lands on the
  // hottest possible arrangement. Stacks by max() so a Frenzy that
  // arrives mid-Boss doesn't dampen the mix.
  setIntensityBoost(mul: number, durationSec: number): void {
    if (!this.app.audio.context) return;
    const now = this.app.audio.context.currentTime;
    const until = now + Math.max(0, durationSec);
    if (mul > this.intensityBoostMul) this.intensityBoostMul = mul;
    if (until > this.intensityBoostUntil) this.intensityBoostUntil = until;
    if (until > this.forceDropUntil) this.forceDropUntil = until;
    // Re-apply mix immediately so the boost is audible without
    // waiting for the next bar.
    if (!this.silenced && this.started) this.applyMix(STD_RAMP_MS);
  }

  // Marks a riser to fire on the next bar boundary. Used by boss
  // intros so the build-up lands exactly on the boss's first
  // downbeat regardless of where the song was in its section flow.
  cueBoss(): void {
    this.bossRiserPending = true;
  }

  // Clamp non-sub layers to 0 (sub-only mix) or restore the normal
  // mix. Persists until cleared so the player doesn't have to keep
  // re-applying it. Only "sub" cap exists today; we leave the param
  // typed for future "drums-only" / "pad-only" expansions.
  setLayerCap(cap: "sub" | null): void {
    if (this.layerCap === cap) return;
    this.layerCap = cap;
    if (!this.silenced && this.started) this.applyMix(STD_RAMP_MS);
  }

  // Driven from MorphHexGame each frame so the intensity boost can
  // expire on time. Cheap when nothing's active.
  update(): void {
    if (this.intensityBoostMul === 1 && this.forceDropUntil === 0) return;
    const ctx = this.app.audio.context;
    if (!ctx) return;
    const now = ctx.currentTime;
    if (now >= this.intensityBoostUntil && this.intensityBoostMul !== 1) {
      this.intensityBoostMul = 1;
      this.intensityBoostUntil = 0;
      if (!this.silenced && this.started) this.applyMix(STD_RAMP_MS);
    }
    if (now >= this.forceDropUntil) {
      this.forceDropUntil = 0;
    }
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    this.composer.dispose();
    this.sub.stop();
    this.wobble.stop();
    this.lead.stop();
    this.pad.stop();
    this.stabs.stop();
    this.started = false;
  }

  // ----------- internals -----------------------------------------------

  private onMusicConfigChanged(cfg: MusicConfig): void {
    if (this.started) this.loadSong(cfg);
  }

  private loadSong(cfg: MusicConfig): void {
    const song = getSong(cfg.songId);
    this.currentSong = song;
    // We deliberately do NOT call beat.setBpm here. MorphHexGame ramps
    // BPM with player speed (120..160) and that "tension" feel is part
    // of the gameplay. Songs are authored on a 16-step grid so they
    // sound musical at any BPM in that range -- the song's `bpm` field
    // is informational only.
    if (song.drumKit) this.beat.setKit(song.drumKit);
    if (song.bassRhythm && song.bassRhythm.length) {
      this.sub.setRhythm(song.bassRhythm);
    }
    // Restart the composer on the new song. Section + bar reset to 0.
    this.composer.start(song);
    this.beat.setSection(this.composer.getCurrentContext()?.section ?? "intro");
    // Re-adopt mix for the new section immediately.
    this.applyMix(/*ramp=*/ STD_RAMP_MS);
  }

  private onComboChanged(combo: number): void {
    const next = comboIntensity(combo);
    if (Math.abs(next - this.intensity) < 0.01) return;
    this.intensity = next;
    if (this.silenced || !this.started) return;
    this.applyMix(STD_RAMP_MS);
  }

  private onBar(ctx: BarContext): void {
    // While a Frenzy/Boss boost forces drop, override any non-drop
    // section the Composer hands us so the Composer doesn't ratchet
    // the mix back down mid-boost. We don't actually mutate the
    // Composer's internal state -- just the section we react to.
    let effectiveSection = ctx.section;
    if (
      this.app.audio.context &&
      this.forceDropUntil > this.app.audio.context.currentTime
    ) {
      effectiveSection = "drop";
    }

    if (effectiveSection !== this.currentSection) {
      this.currentSection = effectiveSection;
      this.beat.setSection(effectiveSection);
      if (this.started && !this.silenced) this.applyMix(STD_RAMP_MS);
    }

    // Riser fires on the LAST bar of a build that's followed by a drop.
    // Triggering on the bar-start gives us a full bar (~1.7s at 140 BPM)
    // of crescendo that lands exactly on the drop's downbeat.
    if (
      ctx.isLastBarOfSection &&
      ctx.section === "build" &&
      ctx.nextSection === "drop" &&
      ctx.time > 0 &&
      this.canUseRiserImpact() &&
      this.app.audio.context
    ) {
      const barSec = (60 / this.beat.getBpm()) * 4;
      this.riser.trigger(ctx.time, barSec * 0.95, 0.55);
    }

    // Boss riser: cued from MorphHexGame onBossStart; lines up with
    // the next bar so the build-up lands on the boss's downbeat.
    if (
      this.bossRiserPending &&
      ctx.time > 0 &&
      this.canUseRiserImpact() &&
      this.app.audio.context
    ) {
      const barSec = (60 / this.beat.getBpm()) * 4;
      this.riser.trigger(ctx.time, barSec * 0.95, 0.6);
      this.bossRiserPending = false;
    }
  }

  private onSectionStart(e: SectionStartEvent): void {
    if (e.time <= 0) return; // priming emit; nothing audible to schedule yet

    // Crash on every section change to mark the boundary musically.
    this.beat.triggerCrash(e.time, 0.45);

    // Impact lands on the drop's downbeat.
    if (e.section === "drop" && this.canUseRiserImpact()) {
      this.impact.trigger(e.time, 0.7);
    }
  }

  private applyMix(rampMs: number): void {
    if (this.silenced) return;
    const base = SECTION_LEVELS[this.currentSection];
    const intensity = this.intensity * this.intensityBoostMul;

    // Sub-only cap zeroes everything except the sub bass. Drums keep
    // playing because they are owned by ProceduralBeat, not by the
    // synth layers below.
    const subOnly = this.layerCap === "sub";

    this.sub.setLevel(base.sub, rampMs);
    this.pad.setLevel(subOnly ? 0 : base.pad, rampMs * 4); // pad rides slower
    this.lead.setLevel(
      subOnly ? 0 : Math.min(1.5, base.lead * intensity),
      rampMs
    );
    this.wobble.setLevel(
      subOnly ? 0 : Math.min(1.5, base.wobble * intensity),
      rampMs
    );
    if (this.canUseStabs()) {
      this.stabs.setLevel(
        subOnly ? 0 : Math.min(1.5, base.stabs * intensity),
        rampMs
      );
    }
    // Pad wants its own slow ramp so transitions don't snap.
    void PAD_RAMP_MS;
  }

  // Quality cap helpers. We keep them inline so the policy lives in
  // one place and is easy to tweak from MusicDirector code.
  private canUseStabs(): boolean {
    return this.quality.tier === "high";
  }

  private canUseRiserImpact(): boolean {
    return this.quality.tier !== "low";
  }

  private canUseLeadTail(): boolean {
    return this.quality.tier !== "low";
  }

  // Exposed for debugging / future menu hooks.
  getCurrentSong(): Song | null {
    return this.currentSong;
  }
}
